import ValidateModule from "./ValidateModule";

import type AjaxRequestBroker from "../AjaxRequestBroker";
import type {IEventChannel, ApiOpts} from "@esWebAPI/es/model/Models";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class GeoMapAnnotationModule
 */
export default class GeoMapAnnotationModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.getAsPngImageUrl = this.getAsPngImageUrl.bind(this);
        this.getByGeoMap = this.getByGeoMap.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
        this.remove = this.remove.bind(this);
        this.getUploadImageUrl = this.getUploadImageUrl.bind(this);
        this.getImageUrl = this.getImageUrl.bind(this);
        this.getImageFilesLimit = this.getImageFilesLimit.bind(this);
        this.listImages = this.listImages.bind(this);
        this.removeImage = this.removeImage.bind(this);
    }

    /**
     * Get the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/' + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * 
     * @param id 
     * 
     * @returns 
     */
    getAsPngImageUrl(id: number): string {
        return es.api.getURL(this.resourceName) + '/png/' + id;
    }
    
    /**
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getByGeoMap(geoMapId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/get_by_geomap/' + geoMapId;
        return this.cache.doGet(this.rqBroker, this.resourceName + '_GeoMap', url, geoMapId, opts).done((rs) => {
            for (let i=0; i<rs.length; i++) {
                this.cache.setEntity(this.resourceName, rs[i]);
            }
        });
    }
    
    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        opts = $.extend({}, opts, {resolveWithXHR: true});
        
        return this.rqBroker.doPost({
            'url': es.api.getURL(this.resourceName),
            'opts': opts,
            'data': resourceObj
        }).done((rs, jqXHR: JQueryXHR) => {
            this.cache.clear(this.resourceName + '_GeoMap');
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("GeoMap", rs.geoMapId);
            /*
            const geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));
            eventChannel?.fireEvent('onCreateGeoMapAnnotation', rs, geoMapLastModified);
            */
            return es.api.appResources.setWorkingCopyChanged(rs.geoMapId).then((geoMap) => {
                this.eventChannel?.fireEvent('onCreateGeoMapAnnotation', rs, geoMap.lastModified);

                return rs;
            });
        });
    }
    
    /**
     * PUT update a resource (note: id attrib must be valid)
     * 
     * @param resourceObj
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        opts = $.extend({}, opts, {resolveWithXHR: true});
        
        return this.rqBroker.doPut({
            'url': es.api.getURL(this.resourceName) + '/' + resourceObj.id,
            'data': resourceObj,
            'opts': opts
        }).done((rs, jqXHR) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("GeoMap", rs.geoMapId);
            /*
            const geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));
            eventChannel?.fireEvent('onUpdateGeoMapAnnotation', rs, geoMapLastModified);
            */
            return es.api.appResources.setWorkingCopyChanged(rs.geoMapId).then((geoMap) => {
                this.eventChannel?.fireEvent('onUpdateGeoMapAnnotation', rs, geoMap.lastModified);

                return rs;
            });
        });
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        opts = $.extend({}, opts, {resolveWithXHR: true});
        
        return this.rqBroker.doDelete({
            'url': es.api.getURL(this.resourceName) + '/' + id,
            'opts': opts
        }).done((rs, jqXHR) => {
            this.cache.clear(this.resourceName + '_GeoMap');
            this.cache.clear(this.resourceName, id);
            this.cache.clear("GeoMap", rs.geoMapId);
            /*
            let geoMapLastModified;
            if (jqXHR.getResponseHeader("es_geomap_last_modified")) {
                geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));
            }
            eventChannel?.fireEvent('onRemoveGeoMapAnnotation', id, geoMapLastModified);
            */
            const geoMapId = parseInt(jqXHR.getResponseHeader("es_geoMap_id"));

            return es.api.appResources.setWorkingCopyChanged(geoMapId).then((geoMap) => {
                this.eventChannel?.fireEvent('onRemoveGeoMapAnnotation', rs, geoMap.lastModified);

                return rs;
            });
        });
    }
    
    /**
     * @returns url where to submit form with image file to be uploaded
     */
    getUploadImageUrl(): string {
        return es.api.getURL(this.resourceName + '/images');
    }
    
    /**
     * url where to get image file (e.g. to use in <img src="XXX">).
     * filename is in the form "username/png_file"
     * 
     * @returns url where to get image file
     */
    getImageUrl(filename: string): string {
        // TODO FIXME More generic way to get full public url?
        return es.publicUrl + 'rest' + es.restVer + '/' + this.resourceName.toLowerCase() + '/images/' + filename + '?TENANT_ID=' + es.tenantId;
    }
    
    /**
     * Get information about the number of stored files
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getImageFilesLimit(opts?: ApiOpts) {
        return this.rqBroker.doGet({
            'url': es.api.getURL(this.resourceName) + '/images/limit',
            'opts': opts
        });
    }
    
    /**
     * Get the list of images available
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    listImages(opts?: ApiOpts) {
        return this.rqBroker.doGet({
            'url': es.api.getURL(this.resourceName) + '/images',
            'opts': opts
        });
    };
    
    /**
     * Get the list of images available
     * 
     * @param filename
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    removeImage(filename: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(filename)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doDelete({
            'url': es.api.getURL(this.resourceName) + '/images/' + filename,
            'opts': opts
        });
    }
}
